import React from "react";
import { useHistory } from "react-router-dom";

import styles from "./HomeProductsWidget.module.scss";
import classNames from "classnames/bind";

import HomeProductsSection from "../../components/HomeProductsSection/HomeProductsSection";
import {
  useGetFeaturedProductsQuery,
  useGetNewestProductsQuery,
  useGetOnSaleProductsQuery,
  useGetCustomersChoiceProductsQuery,
} from "../../providers/products";
import { useConfig } from "../../providers/config";
import { HomeProductsWidgetSkeleton } from "../HomeProductsWidgetSkeleton/HomeProductsWidgetSkeleton";
import { AppSettings } from "@core/types";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const HomeProductsWidget = () => {
  const { activeShippingZone } = useActiveOrMainShippingZone();

  const {
    data: newestProductList,
    isFetching: isNewestProductsFetching,
    isRefetching: isNewestProductRefetching,
  } = useGetNewestProductsQuery(
    {
      zoneId: activeShippingZone.id,
    },
    { enabled: !!activeShippingZone.id },
  );

  const {
    data: onSaleProductList,
    isFetching: isOnSaleProductsFetching,
    isRefetching: isOnSaleProductRefetching,
  } = useGetOnSaleProductsQuery(
    {
      zoneId: activeShippingZone.id,
    },
    { enabled: !!activeShippingZone.id },
  );

  const {
    data: featuredProductList,
    isFetching: isFeatureProductsFetching,
    isRefetching: isFeatureProductRefetching,
  } = useGetFeaturedProductsQuery(
    {
      zoneId: activeShippingZone.id,
    },
    { enabled: !!activeShippingZone.id },
  );

  const {
    data: customersChoiceProductsList,
    isFetching: isCustomersChoiceProductsFetching,
    isRefetching: isCustomersChoiceProductsRefetching,
  } = useGetCustomersChoiceProductsQuery(
    {
      zoneId: activeShippingZone.id,
    },
    { enabled: !!activeShippingZone.id },
  );

  const featuredProducts = featuredProductList?.products ?? [];
  const customersChoiceProducts = customersChoiceProductsList?.products ?? [];
  const onSaleProducts = onSaleProductList?.products ?? [];
  const newestProducts = newestProductList?.products ?? [];

  const { t } = useTranslation();
  const config = useConfig();

  const { homePageCategories: { featured, latest, sale, customersChoice } = {} } = config ?? ({} as AppSettings);

  let history = useHistory();

  const openProducts = (value: any) => {
    history.push("/products/0/0/" + value);
  };

  return (
    <div className={cx("homePage")}>
      {isFeatureProductsFetching || isFeatureProductRefetching ? (
        <HomeProductsWidgetSkeleton />
      ) : (
        featured?.isEnabled &&
        !!featuredProducts?.length && (
          <HomeProductsSection
            sectionTitle={t(`${featured?.title}`)}
            buttonTitle={t("more")}
            items={featuredProducts}
            openProducts={() => {
              openProducts("featured");
            }}
          />
        )
      )}
      {isCustomersChoiceProductsFetching || isCustomersChoiceProductsRefetching ? (
        <HomeProductsWidgetSkeleton />
      ) : (
        customersChoice?.isEnabled &&
        !!customersChoiceProducts?.length && (
          <HomeProductsSection
            sectionTitle={t(`${customersChoice?.title}`)}
            buttonTitle={t("more")}
            items={customersChoiceProducts}
            openProducts={() => {
              openProducts("popular");
            }}
          />
        )
      )}

      {isOnSaleProductsFetching || isOnSaleProductRefetching ? (
        <HomeProductsWidgetSkeleton />
      ) : (
        sale?.isEnabled &&
        !!onSaleProducts?.length && (
          <HomeProductsSection
            sectionTitle={t(`${sale?.title}`)}
            buttonTitle={t("more")}
            items={onSaleProducts}
            openProducts={() => {
              openProducts("sale");
            }}
          />
        )
      )}

      {isNewestProductsFetching || isNewestProductRefetching ? (
        <HomeProductsWidgetSkeleton />
      ) : (
        latest?.isEnabled &&
        !!newestProducts?.length && (
          <HomeProductsSection
            sectionTitle={t(`${latest?.title}`)}
            buttonTitle={t("more")}
            items={newestProducts}
            openProducts={() => {
              openProducts("latest");
            }}
          />
        )
      )}
    </div>
  );
};

export default HomeProductsWidget;
